import find from 'lodash/find';

// API
import api from '../../utils/api';

// Actions
import { createMarketingLead, createMarketUpdate } from '../actions/MarketingActions';

// Queries
import { getCompsOnlyQuery, getFullQuery } from '../../utils/queries';
// Misc / utils
import pathsManifest from '../../utils/pathsManifest';
import { encodeFullAddressForUrl } from '../../utils/urlUtils';
import routingUtils from '../../utils/routingUtils';
// @ts-ignore
import { augmentError } from '../../utils/errors';
// @ts-ignore
import constants from '../constants';
import errorConstants from '../../constants/ErrorConstants';

// Models
import AutocompleteAddress from '../../models/AutocompleteAddress';
import Address from '../../models/Address';
import Comparables from '../../models/Comparables';
import AddressDetails from '../../models/AddressDetails';

// service
// import appLogger from 'app/shared/loggers/appLogger';

// Types
import type {
  APIResult,
  AutocompleteAddressApiType,
  ByAddressApiType,
  ComparablesApiType,
  getListingForAddressApiType,
  ListingApiErrorType,
} from '../../types/ApiResponseTypes';
import type { QueryDataObjectType } from '../../types/QueryDataObjectType';
import type { UserProps } from '../../models/User';
import type { State } from '../../types/State';
import { emitRentZestimateEvent } from '../../analytics/gtmEvents';
import { emitErrorEvent, emitSuccessEvent } from '../../analytics/priceMyRentalEvents';

export type AugmentedAutocompleteAddressType = AutocompleteAddress & {
  isRecent: boolean;
  unit: string;
};

// ActionCreators... <verb><Noun>
export const goToAddressItem = (
  addressDataObject: AugmentedAutocompleteAddressType,
  user: UserProps,
  urlQueryObject: Record<string, string>,
  addRecentSearch: (arg: AugmentedAutocompleteAddressType) => void,
  setAddress: (arg: Address) => void,
  address: Address,
) => {
  return async () => {
    try {
      const userEmail = user.email;

      addRecentSearch(addressDataObject);

      // Call marketing lead API
      createMarketingLead({
        fullAddress: addressDataObject.name || '',
        unit: addressDataObject.unit,
        email: userEmail,
        urlQueryObject,
      });

      // Fetch address and market summary and comparables
      const { userAddress } = await getAddressAndComparables(addressDataObject, user, setAddress, address)();

      // Call marketing update API
      createMarketUpdate({
        email: userEmail,
        zpid: userAddress.zpid,
        price: userAddress.rentZestimate,
        lat: userAddress.lat,
        lon: userAddress.lon,
      });

      const encodedFullAddress = encodeFullAddressForUrl({
        address: addressDataObject.name,
        unit: addressDataObject.unit,
      });

      emitSuccessEvent({ user });
      emitRentZestimateEvent();

      return routingUtils.push(`${pathsManifest.PriceMyRentalPageResults}/${encodedFullAddress}`);
    } catch (err) {
      const shouldLogAction = !err.data || !err.data.action;

      augmentError(err, {
        ...(shouldLogAction && {
          action: 'Error in AddressActions#goToAddressItem',
        }),
      });

      throw err;
    }
  };
};

export const getComparables = (params: QueryDataObjectType, user: UserProps, address: Address) => {
  return async () => {
    // Call partial query from API that gets comparables
    const requestBody = getCompsOnlyQuery(params);
    const { data } = await fetchGraphQlData(requestBody, user)();
    const userComps = setComparables(data, user, address);

    return { userComps };
  };
};

export const getAddressAndComparables = (
  params: AugmentedAutocompleteAddressType,
  user: UserProps,
  setAddress: (arg: Address) => void,
  address: Address,
) => {
  // @ts-ignore
  return async () => {
    // Call full query from API
    const requestBody = getFullQuery(params);
    const { data, errors } = await fetchGraphQlData(requestBody, user)();
    const userAddress = await _setAddress(data, user, setAddress)();
    const hasZestimate = !find(errors, (error) => error.code === errorConstants.RENT_ZESTIMATE_NOT_FOUND);
    const hasCompsPrices = !find(errors, (error) => error.code === errorConstants.GET_COMPS_PRICE_EMPTY);

    if (!hasCompsPrices && !hasZestimate) {
      throwNonCriticalError({
        message: "We don't have enough data for this address",
        code: errorConstants.RENT_ZESTIMATE_NOT_FOUND,
      });
    }

    if (hasCompsPrices) {
      setComparables(data, user, address)();
    }

    return {
      userAddress,
    };
  };
};

// API fetches
export const fetchAutocompleteData = (partialAddress: string) => {
  return async () => {
    try {
      const data = (await api.address.autocomplete(partialAddress)) as { addresses: Array<AutocompleteAddressApiType> };
      const addressSuggestions = data.addresses.map((address: AutocompleteAddressApiType) => {
        return new AutocompleteAddress(address);
      });

      return addressSuggestions;
    } catch (error) {
      augmentError(error, {
        action: 'Error in AddressActions#fetchAutocompleteData',
      });

      throw error;
    }
  };
};

export const fetchGraphQlData = (requestBody: string, user: UserProps) => {
  return async () => {
    try {
      const { data, errors } = (await api.address.graphQlListing(requestBody)) as {
        data: ByAddressApiType;
        errors: Array<ListingApiErrorType>;
      };

      /*
       * Throw on major errors, otherwise handle errors in places where they cause issues
       */
      if (errors.length > 0) {
        if (containsError(errors, errorConstants.PROPERTY_DETAILS_NOT_FOUND)) {
          // If listing can't be found by our API, throw error and STOP
          emitErrorEvent({ user });
          throwNonCriticalError({
            message: 'We were unable to match this address',
            code: errorConstants.PROPERTY_DETAILS_NOT_FOUND,
          });
        }

        if (containsError(errors, errorConstants.GET_COMPS_PRICE_EMPTY)) {
          emitErrorEvent({ user });
        }

        if (containsError(errors, errorConstants.RENT_ZESTIMATE_NOT_FOUND)) {
          emitErrorEvent({ user });
        }
      }

      return { data, errors };
    } catch (error) {
      augmentError(error, {
        action: 'Error in AddressActions#fetchGraphQlData',
      });

      throw error;
    }
  };
};

// Setters
export const _setAddress = (data: ByAddressApiType, user: UserProps, setAddress: (arg: Address) => void) => {
  return async () => {
    const addressData = {
      ...data.byAddress.floorplans[0],
      geo: data.byAddress.geo,
      marketSummary: data.byAddress?.marketSummary,
    };

    // Check if the user logged in - if so, check if they already have a listing for this address
    const isUserLoggedIn: boolean = user.loggedIn;

    if (isUserLoggedIn) {
      const { street, city, state, zip, unit } = addressData.address;
      const fullAddress = street + ',' + city + ', ' + state + ' ' + zip;

      await api.address
        .getListingForAddress(fullAddress, unit)
        .then((response: unknown) => {
          const res = response as getListingForAddressApiType;

          addressData.addressHasListing = res.listingFound;
          // If listingFound false, then there is no listing (a.k.a undefined)
          if (res.listingFound) {
            addressData.zrmListingId = res.listing.listingId;
          }
          return;
        })
        .catch((error: { message: string }) => {
          augmentError(error, {
            action: 'Error in AddressActions#_setAddress',
          });

          throw error;
        });
    }

    // Set the user's address
    const modeledAddressData = new Address(addressData);

    setAddress(modeledAddressData);

    return modeledAddressData;
  };
};

export const setComparables = (data: ByAddressApiType, user: UserProps, address: Address) => {
  return () => {
    const { floorplans, listingRanking } = data.byAddress.similarFloorplans;

    if (floorplans.length === 0) {
      emitErrorEvent({ user });
    }

    // Set the user's comparables
    const compsItems = floorplans.map((comp: ComparablesApiType) => {
      return new Comparables(comp);
    });

    const userProperty = address as unknown as State['address'];
    compsItems.splice(listingRanking, 0, userProperty as unknown as Comparables);

    return compsItems;
  };
};

// Errors
const throwNonCriticalError = ({ message, code }: { message: string; code: string }) => {
  throw {
    nonCriticalError: true,
    errorCode: code,
    errorMsg: message,
  };
};

const containsError = (errors: Array<ListingApiErrorType>, errorConstant: string) => {
  return find(errors, (error) => error.code === errorConstant);
};

const AddressActions = {
  getDetails(
    addressData: { address: string; unit: string },
    setAddressDetails: (arg: ReturnType<typeof AddressDetails>) => void,
  ) {
    return async () =>
      await api.properties.address
        .details(addressData)
        .then((response: unknown) => {
          const { error: errorRMWModel, success, response: getDetailsResponse } = response as APIResult;
          if (success && getDetailsResponse) {
            const { status, message } = getDetailsResponse;

            if (status && (status as string).toLowerCase() === constants.ADDRESS_STATUS_FAILURE.toLowerCase()) {
              throw Promise.reject(message);
            }

            const addressDetailsModel = AddressDetails(getDetailsResponse);

            setAddressDetails(addressDetailsModel);

            return addressDetailsModel;
          }
          throw Promise.reject(errorRMWModel);
        })
        .catch((errorRMWModel: object) => {
          // appLogger.logError({
          //   message: errorRMWModel.message,
          //   context: 'AddressActions.getDetails',
          // }); // logger is not supported on ZMC yet

          throw Promise.reject(errorRMWModel);
        });
  },
  verify(addressData: { address: string; unit: string; propertyTypeCode?: number }) {
    return async () =>
      await api.properties.address
        .verification({
          address: addressData.address,
          unit: addressData.unit,
          propertyTypeCode: addressData.propertyTypeCode,
        })
        .then((res: unknown) => {
          const response = res as APIResult;
          if (response.success && response.response) {
            return response.response;
          }

          throw Promise.reject(response.error);
        });
  },
  getRestrictions(addressData: {
    address: string;
    unit: string;
    listingTypeCode: number;
    listingAlias?: string;
    propertyTypeCode: number | null;
  }) {
    const { address, unit, listingTypeCode, listingAlias, propertyTypeCode } = addressData;

    return async () =>
      await api.properties.locations
        .getRestrictions({
          address,
          unit,
          listingTypeCode,
          listingAlias,
          propertyTypeCode,
        })
        .then((res: unknown) => {
          const response = res as APIResult;
          const { error: errorRMWModel, success } = response;

          if (success && response.response) {
            return response.response;
          }

          throw Promise.reject(errorRMWModel);
        })
        .catch((errorRMWModel: object) => {
          // appLogger.logError({
          //   message: errorRMWModel.message,
          //   context: 'AddressActions.getRestrictions',
          // }); // logger is not supported on ZMC yet

          throw Promise.reject(errorRMWModel);
        });
  },
  getPublicRestrictions(addressData: {
    address: string;
    unit: string;
    listingTypeCode: number;
    listingAlias?: string;
    propertyTypeCode: number | null;
  }) {
    const { address, unit, listingTypeCode, listingAlias, propertyTypeCode } = addressData;

    return async () =>
      await api.properties.locations
        .getPublicRestrictions({
          address,
          unit,
          listingTypeCode,
          listingAlias,
          propertyTypeCode,
        })
        .then((res: unknown) => {
          const response = res as APIResult;
          const { error: errorRMWModel, success } = response;

          if (success && response.response) {
            return response.response;
          }

          throw Promise.reject(errorRMWModel);
        })
        .catch((errorRMWModel: object) => {
          // appLogger.logError({
          //   message: errorRMWModel.message,
          //   context: 'AddressActions.getPublicRestrictions',
          // }); // logger is not supported on ZMC yet

          throw Promise.reject(errorRMWModel);
        });
  },
};

export default AddressActions;
